import React, { useState } from 'react';
import { X, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/core/ui/components/Button';
import { Input } from '@/core/ui/components/Input';
import { useServices, useToast } from '@/app/providers';
import { useI18n } from '@/shared/i18n';

interface ChangePasswordModalProps {
  onClose: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ onClose }) => {
  const { t } = useI18n();
  const toast = useToast();
  const { userRepo } = useServices();

  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  // Toggle visibility states
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (!currentPassword || !newPassword || !confirmPassword) {
      return;
    }

    if (newPassword.length < 6) {
      setError(t('profile.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('profile.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      await userRepo.changePassword(currentPassword, newPassword);
      toast.success(t('profile.passwordChanged'));
      onClose();
    } catch (err: unknown) {
      // Si el error es de contraseña incorrecta
      const errorMessage = err instanceof Error ? err.message : '';
      if (errorMessage.includes('incorrect') || errorMessage.includes('incorrecta') || errorMessage.includes('400')) {
        setError(t('profile.incorrectPassword'));
      } else {
        setError(errorMessage || 'Error al cambiar la contraseña');
      }
    } finally {
      setLoading(false);
    }
  };

  const PasswordToggle = ({ show, onToggle }: { show: boolean; onToggle: () => void }) => (
    <button
      type="button"
      onClick={onToggle}
      className="absolute inset-y-0 right-3 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors mt-6"
    >
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Lock size={20} className="text-primary-600" />
            </div>
            <h3 className="font-bold text-slate-800 text-lg">{t('profile.changePassword')}</h3>
          </div>
          <button onClick={onClose}>
            <X size={20} className="text-slate-400 hover:text-slate-600 transition-colors" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="relative flex items-center">
            <Input
              label={t('profile.currentPassword')}
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <PasswordToggle show={showCurrent} onToggle={() => setShowCurrent(!showCurrent)} />
          </div>

          <div className="relative flex items-center">
            <Input
              label={t('profile.newPassword')}
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            <PasswordToggle show={showNew} onToggle={() => setShowNew(!showNew)} />
          </div>
          <p className="text-xs text-slate-500 -mt-2">{t('profile.passwordTooShort').replace('debe tener', 'debe tener').replace('must be', 'Must be')}</p>

          <div className="relative flex items-center">
            <Input
              label={t('profile.confirmPassword')}
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            <PasswordToggle show={showConfirm} onToggle={() => setShowConfirm(!showConfirm)} />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" className="flex-1" isLoading={loading} icon={<Lock size={16} />}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
